import { Minus, Plus, ThumbsUp } from 'lucide-react';
import type { RetroSnapshot, VoteTarget } from '@retro/shared';
import { useMyVotes, useRetroStore, useVotesUsed } from '../../lib/retroStore.js';
import { BoardColumn, BoardTrack } from './BoardLayout.js';
import { TopicChipList } from './TopicChips.js';
import { CommentAuthor } from './CommentCard.js';

function VoteStepper({ target, disabled }: { target: VoteTarget; disabled: boolean }) {
  const setVote = useRetroStore((state) => state.setVote);
  const perParticipant = useRetroStore((state) => state.snapshot?.votes.votesPerParticipant ?? 0);
  const mine = useMyVotes(target);
  const used = useVotesUsed();

  const outOfVotes = used >= perParticipant;

  return (
    <div className="stepper">
      <button
        type="button"
        disabled={disabled || mine === 0}
        onClick={() => void setVote(target, mine - 1)}
        className="btn btn-sm"
        aria-label="Remove a vote"
      >
        <Minus size={14} aria-hidden />
      </button>
      <output aria-live="polite">
        {mine} {mine === 1 ? 'vote' : 'votes'}
      </output>
      <button
        type="button"
        disabled={disabled || outOfVotes}
        onClick={() => void setVote(target, mine + 1)}
        className="btn btn-sm btn-primary"
        aria-label="Add a vote"
      >
        <Plus size={14} aria-hidden /> Vote
      </button>
    </div>
  );
}

/**
 * Step 3. Cards are shown without authors so the vote is about the idea rather
 * than who raised it. Running totals stay hidden until the next step (see the
 * server's snapshot builder) to prevent bandwagoning.
 */
export function VotePhase({ snapshot }: { snapshot: RetroSnapshot }) {
  const used = useVotesUsed();
  const { votesPerParticipant } = snapshot.votes;
  const votable = snapshot.retro.settings.vote.votableColumnIds;

  return (
    <>
      <div className="filters">
        <span className="badge">
          <ThumbsUp size={12} aria-hidden /> Your votes {used}/{votesPerParticipant}
        </span>
        <span className="badge">
          Team votes {snapshot.votes.teamVotesUsed}/{snapshot.votes.teamVotesTotal}
        </span>
        <span className="text-[12px] text-muted">Nobody can see who voted for what.</span>
      </div>

      <BoardTrack>
        {snapshot.columns.map((column, index) => {
          const columnComments = snapshot.comments.filter((comment) => comment.columnId === column.id);
          const groups = snapshot.groups
            .filter((group) => group.columnId === column.id)
            .sort((a, b) => a.order - b.order);
          const ungrouped = columnComments
            .filter((comment) => !comment.groupId)
            .sort((a, b) => a.order - b.order);

          const locked = votable !== null && !votable.includes(column.id);

          return (
            <BoardColumn
              key={column.id}
              column={column}
              index={index}
              badge={
                locked ? (
                  <span className="badge" title="Voting is disabled for this column">
                    no votes
                  </span>
                ) : (
                  <span className="badge">{groups.length + ungrouped.length}</span>
                )
              }
            >
              {groups.map((group) => {
                const members = columnComments.filter((comment) => comment.groupId === group.id);
                if (members.length === 0) return null;

                return (
                  <div
                    key={group.id}
                    role="group"
                    aria-label={`Group of ${members.length} cards`}
                    className="group"
                  >
                    <div className="group-h">
                      <span className="badge">{members.length}</span>
                      <span className="min-w-0 flex-1 truncate">
                        {group.title || 'Grouped comments'}
                      </span>
                    </div>

                    <div className="grid gap-1.5">
                      {members.map((comment) => (
                        <article key={comment.id} className="note">
                          <div className="note-a">
                            <CommentAuthor comment={comment} />
                          </div>
                          <p className="note-t">{comment.text}</p>
                        </article>
                      ))}
                    </div>

                    <div className="flex items-end justify-between gap-2 px-1">
                      <TopicChipList
                        topicIds={[...new Set(members.flatMap((comment) => comment.topicIds))]}
                        topics={snapshot.topics}
                      />
                      <VoteStepper target={{ type: 'GROUP', id: group.id }} disabled={locked} />
                    </div>
                  </div>
                );
              })}

              {ungrouped.map((comment) => (
                <article key={comment.id} className="note">
                  <div className="note-a">
                    <CommentAuthor comment={comment} />
                  </div>
                  <p className="note-t">{comment.text}</p>
                  <div className="flex items-end justify-between gap-2">
                    <TopicChipList topicIds={comment.topicIds} topics={snapshot.topics} />
                    <VoteStepper target={{ type: 'COMMENT', id: comment.id }} disabled={locked} />
                  </div>
                </article>
              ))}

              {groups.length + ungrouped.length === 0 ? (
                <p className="px-1 py-2 text-[12px] text-muted">No cards in this column.</p>
              ) : null}
            </BoardColumn>
          );
        })}
      </BoardTrack>
    </>
  );
}
